import React, { ReactElement } from 'react'
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'

export let ANCC_TARGET_AUDIENCES = [
  'Registered Nurse',
  'Paramedic',
  'EMT',
  'Respiratory Therapist',
  'Advanced Practice Nurse',
  'Other',
]

export let ANCC_EDUCATIONAL_NEEDS_OPTIONS = ['Knowledge', 'Skill', 'Practice']

type FormFieldProps = {
  field: any
}

export let CourseCodeField = ({ field }: FormFieldProps): ReactElement => (
  <TextField
    label="Course Code"
    name={field.name}
    value={field.state.value}
    onChange={(e) => field.handleChange(e.target.value)}
    onBlur={field.handleBlur}
    fullWidth
    sx={{ bgcolor: 'background.paperDark' }}
  />
)

export let ExpirationDateField = ({ field }: FormFieldProps): ReactElement => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      label="Expiration Date"
      value={field.state.value ? dayjs(field.state.value) : null}
      onChange={(val) =>
        field.handleChange(val ? val.format('YYYY-MM-DD') : '')
      }
      onClose={field.handleBlur}
      slotProps={{
        textField: {
          fullWidth: true,
          sx: { bgcolor: 'background.paperDark' },
          onBlur: field.handleBlur,
        },
      }}
    />
  </LocalizationProvider>
)

export let TargetAudienceField = ({ field }: FormFieldProps): ReactElement => (
  <FormControl fullWidth required>
    <InputLabel>Target Audience</InputLabel>
    <Select
      multiple
      value={field.state.value}
      label="Target Audience"
      onChange={(e) => field.handleChange(e.target.value)}
      onBlur={field.handleBlur}
      sx={{ bgcolor: 'background.paperDark' }}
      input={<OutlinedInput label="Target Audience" />}
      renderValue={(selected) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {Array.isArray(selected) &&
            selected.map((v) => <Chip key={v} label={v} size="small" />)}
        </Box>
      )}
    >
      {ANCC_TARGET_AUDIENCES.map((a) => (
        <MenuItem key={a} value={a}>
          {a}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)

export let EducationalNeedsField = ({
  field,
}: FormFieldProps): ReactElement => (
  <FormControl fullWidth required>
    <InputLabel>Educational Needs</InputLabel>
    <Select
      multiple
      value={field.state.value}
      label="Educational Needs"
      onChange={(e) => field.handleChange(e.target.value)}
      onBlur={field.handleBlur}
      sx={{ bgcolor: 'background.paperDark' }}
      input={<OutlinedInput label="Educational Needs" />}
      renderValue={(selected) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {Array.isArray(selected) &&
            selected.map((v) => <Chip key={v} label={v} size="small" />)}
        </Box>
      )}
    >
      {ANCC_EDUCATIONAL_NEEDS_OPTIONS.map((n) => (
        <MenuItem key={n} value={n}>
          {n}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
)

export let CompetencyField = ({ field }: FormFieldProps): ReactElement => (
  <TextField
    label="Competency (Optional)"
    name={field.name}
    value={field.state.value}
    onChange={(e) => field.handleChange(e.target.value)}
    onBlur={field.handleBlur}
    multiline
    rows={3}
    fullWidth
    sx={{ bgcolor: 'background.paperDark' }}
    placeholder="Clinical competency standards"
  />
)

export let ProfessionalSourceField = ({
  field,
}: FormFieldProps): ReactElement => (
  <TextField
    label="Professional Source (Optional)"
    name={field.name}
    value={field.state.value}
    onChange={(e) => field.handleChange(e.target.value)}
    onBlur={field.handleBlur}
    multiline
    rows={3}
    fullWidth
    sx={{ bgcolor: 'background.paperDark' }}
    placeholder="American Nurses Association or other professional source"
  />
)

export let NotesField = ({ field }: FormFieldProps): ReactElement => (
  <TextField
    label="Notes"
    name={field.name}
    value={field.state.value}
    onChange={(e) => field.handleChange(e.target.value)}
    onBlur={field.handleBlur}
    multiline
    rows={3}
    fullWidth
    sx={{ bgcolor: 'background.paperDark' }}
    placeholder="Add any notes or comments about this pending accreditation"
  />
)

export let FieldsRow = ({
  children,
}: {
  children: React.ReactNode
}): ReactElement => <Box className="grid grid-cols-2 gap-4">{children}</Box>
